import { useEffect, useMemo, useRef, useState } from "react";
import { animate, motion, useInView, useMotionValue, useReducedMotion, useTransform } from "motion/react";
import { EASE_OUT, MaskWords } from "../../../kit.jsx";
import { AURORA_COUNTRIES, AURORA_REPORTED_COUNTRIES as COUNTRY_TOTAL, Flag } from "../../../components/flags.jsx";

const MORE = COUNTRY_TOTAL - AURORA_COUNTRIES.length;

const W = 960;
const H = 480;
const DELHI = [77.2, 28.6];
// ISO 3166 numeric ids used by world-atlas. Singapore is too small for the 110m map, so it gets a pin only.
const ISO = { au: "036", az: "031", bg: "100", ca: "124", cn: "156", de: "276", gh: "288", in: "356", ma: "504", ng: "566", sn: "686", sg: null, za: "710", ua: "804", us: "840" };
const STEP = 0.24;

function distance([a, b], [c, d]) {
  return Math.hypot(a - c, b - d);
}

// India first, then outward from Delhi.
const ORDER = [...AURORA_COUNTRIES].sort((a, b) => {
  if (a.code === "in") return -1;
  if (b.code === "in") return 1;
  return distance(a.lonLat, DELHI) - distance(b.lonLat, DELHI);
});

function useMapGeometry(load) {
  const [geometry, setGeometry] = useState(null);
  useEffect(() => {
    if (!load || geometry) return undefined;
    let cancelled = false;
    Promise.all([
      import("world-atlas/countries-110m.json"),
      import("topojson-client"),
      import("d3-geo"),
    ]).then(([atlasModule, topo, geo]) => {
      if (cancelled) return;
      const atlas = atlasModule.default ?? atlasModule;
      const all = topo.feature(atlas, atlas.objects.countries).features.filter((f) => f.id !== "010");
      const projection = geo.geoNaturalEarth1().fitExtent([[8, 8], [W - 8, H - 8]], { type: "FeatureCollection", features: all });
      const path = geo.geoPath(projection);
      const named = new Set(Object.values(ISO).filter(Boolean));
      const origin = projection(DELHI);
      const countries = {};
      all.forEach((f) => {
        if (named.has(f.id)) countries[f.id] = path(f);
      });
      const base = all.filter((f) => !named.has(f.id)).map((f) => path(f)).join(" ");
      const pins = {};
      AURORA_COUNTRIES.forEach((c) => {
        const [x, y] = projection(c.lonLat);
        const dx = x - origin[0];
        const len = Math.hypot(dx, y - origin[1]) || 1;
        const lift = Math.min(140, len * 0.32);
        const cx = (x + origin[0]) / 2;
        const cy = Math.min(y, origin[1]) - lift * 0.8;
        pins[c.code] = { x, y, arc: `M ${origin[0].toFixed(1)} ${origin[1].toFixed(1)} Q ${cx.toFixed(1)} ${cy.toFixed(1)} ${x.toFixed(1)} ${y.toFixed(1)}` };
      });
      setGeometry({ base, countries, pins, origin });
    });
    return () => {
      cancelled = true;
    };
  }, [load, geometry]);
  return geometry;
}

function Country({ d, index, play, still }) {
  return (
    <motion.path
      d={d}
      className="ab-map__named"
      initial={still ? false : { fill: "#eeeeee" }}
      animate={play || still ? { fill: "#a9d7ff" } : { fill: "#eeeeee" }}
      transition={{ duration: 0.5, delay: index * STEP, ease: EASE_OUT }}
    />
  );
}

function Pin({ country, pin, index, play, still }) {
  const isHome = country.code === "in";
  const delay = index * STEP;
  return (
    <g>
      {!isHome && (
        <motion.path
          className="ab-map__arc"
          d={pin.arc}
          initial={still ? false : { pathLength: 0, opacity: 0 }}
          animate={play || still ? { pathLength: 1, opacity: 1 } : { pathLength: 0, opacity: 0 }}
          transition={{ duration: 0.8, delay, ease: [0.65, 0, 0.35, 1] }}
        />
      )}
      <g transform={`translate(${pin.x.toFixed(1)} ${pin.y.toFixed(1)})`}>
        <motion.g
          className="ab-map__pin"
          initial={still ? false : { scale: 0, opacity: 0 }}
          animate={play || still ? { scale: 1, opacity: 1 } : { scale: 0, opacity: 0 }}
          transition={{ duration: 0.5, delay: delay + (isHome ? 0.1 : 0.62), ease: EASE_OUT }}
        >
          <circle r="11.5" className="ab-map__ring" />
          <image href={country.round} x="-9.5" y="-9.5" width="19" height="19" clipPath="url(#ab-pin-clip)" preserveAspectRatio="xMidYMid slice" />
        </motion.g>
      </g>
    </g>
  );
}

export default function WorldMap() {
  const ref = useRef(null);
  const reduceMotion = useReducedMotion();
  const near = useInView(ref, { once: true, margin: "900px 0px 900px 0px" });
  const play = useInView(ref, { once: true, amount: 0.4 });
  const geometry = useMapGeometry(near);
  const count = useMotionValue(reduceMotion ? COUNTRY_TOTAL : 0);
  const countText = useTransform(count, (value) => (value >= COUNTRY_TOTAL ? `${COUNTRY_TOTAL}+` : String(Math.round(value))));
  const ready = Boolean(geometry);

  useEffect(() => {
    if (reduceMotion || !play || !ready) return undefined;
    // Counts along with the flags as they land, then finishes on the total.
    const controls = animate(count, [0, AURORA_COUNTRIES.length, COUNTRY_TOTAL], {
      duration: ORDER.length * STEP + 1.2,
      times: [0, (ORDER.length * STEP + 0.4) / (ORDER.length * STEP + 1.2), 1],
      ease: ["linear", EASE_OUT],
    });
    return () => controls.stop();
  }, [count, play, ready, reduceMotion]);

  const orderIndex = useMemo(() => Object.fromEntries(ORDER.map((c, i) => [c.code, i])), []);

  return (
    <section className="ab-map" id="map" aria-labelledby="ab-map-title">
      <header className="ab-map__head">
        <div>
          <p className="ab-kicker">Aurora, around the world</p>
          <MaskWords id="ab-map-title" className="ab-h2" text={`Participants from ${COUNTRY_TOTAL}+ countries.`} />
        </div>
        <div className="ab-map__counter" aria-live="polite">
          <span className="sr-only">{COUNTRY_TOTAL}+ countries</span>
          <p aria-hidden="true">
            <motion.strong>{countText}</motion.strong>
            <span>countries</span>
          </p>
        </div>
      </header>
      <div className="ab-map__frame" ref={ref}>
        <svg viewBox={`0 0 ${W} ${H}`} role="img" aria-label={`World map. ${AURORA_COUNTRIES.map((c) => c.name).join(", ")} are highlighted, with lines from Delhi.`}>
          <defs>
            <clipPath id="ab-pin-clip"><circle r="9.5" /></clipPath>
          </defs>
          {ready && (
            <>
              <path className="ab-map__land" d={geometry.base} />
              {ORDER.map((country) =>
                ISO[country.code] && geometry.countries[ISO[country.code]] ? (
                  <Country key={country.code} d={geometry.countries[ISO[country.code]]} index={orderIndex[country.code]} play={play} still={reduceMotion} />
                ) : null,
              )}
              {ORDER.map((country) => (
                <Pin key={country.code} country={country} pin={geometry.pins[country.code]} index={orderIndex[country.code]} play={play} still={reduceMotion} />
              ))}
              <g transform={`translate(${geometry.origin[0].toFixed(1)} ${geometry.origin[1].toFixed(1)})`} className="ab-map__origin">
                <circle r="4" />
                <text x="-14" y="4" textAnchor="end">Delhi</text>
              </g>
            </>
          )}
        </svg>
        {!ready && <p className="ab-map__loading">Loading the map</p>}
      </div>
      <div className="ab-map__foot">
        <p className="ab-body">
          Aurora runs online, and its participants came from {COUNTRY_TOTAL}+ countries.
        </p>
        <ul className="ab-flags" aria-label={`Aurora countries, ${COUNTRY_TOTAL}+ in total`}>
          {AURORA_COUNTRIES.map((country, index) => (
            <motion.li
              key={country.code}
              initial={reduceMotion ? false : { opacity: 0, y: 12 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, amount: 0.5 }}
              transition={{ duration: 0.6, delay: (index % 5) * 0.05 + Math.floor(index / 5) * 0.08, ease: EASE_OUT }}
            >
              <Flag code={country.code} />
              <span>{country.name}</span>
            </motion.li>
          ))}
          <motion.li
            className="ab-flags__more-item"
            initial={reduceMotion ? false : { opacity: 0, y: 12 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.5 }}
            transition={{ duration: 0.6, delay: 0.32, ease: EASE_OUT }}
          >
            <span className="ab-flags__more">+{MORE}</span>
            <span>more countries</span>
          </motion.li>
        </ul>
      </div>
    </section>
  );
}
